/**
 * Dual-handle price range facet.
 *
 * Replaces the stock min/max text inputs. Unlike the rest of the facets, this
 * one navigates instead of updating the section over XHR, so the price floor
 * below can be applied on the way in.
 */

const DEBOUNCE_MS = 700;

class PriceSliderFacet extends HTMLElement {
  static floorEnforced = false;

  connectedCallback() {
    this.minRange = this.querySelector('[data-slider-min]');
    this.maxRange = this.querySelector('[data-slider-max]');
    this.activeTrack = this.querySelector('[data-slider-active-track]');
    this.minDisplay = this.querySelector('[data-slider-min-display]');
    this.maxDisplay = this.querySelector('[data-slider-max-display]');

    if (!this.minRange || !this.maxRange || !this.activeTrack) return;

    this.symbol = this.dataset.currencySymbol || '';
    this.formatter = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    this.handleInput = this.handleInput.bind(this);
    this.minRange.addEventListener('input', this.handleInput);
    this.maxRange.addEventListener('input', this.handleInput);

    this.paint();
    this.enforceFloor();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  /**
   * Collections that don't derive their floor from their own cheapest product
   * get a hard one, so a first visit carrying no price params is redirected to
   * the floored URL. Only the first instance on the page acts on this.
   */
  enforceFloor() {
    const floor = this.dataset.enforceFloor;
    if (!floor || PriceSliderFacet.floorEnforced) return;

    const url = new URL(window.location.href);
    if (url.searchParams.has('filter.v.price.gte')) return;

    PriceSliderFacet.floorEnforced = true;
    url.searchParams.set('filter.v.price.gte', floor);
    window.location.replace(url.toString());
  }

  handleInput() {
    this.paint();
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.submit(), DEBOUNCE_MS);
  }

  paint() {
    const rangeMin = parseFloat(this.minRange.min);
    const rangeMax = parseFloat(this.minRange.max);
    let low = parseFloat(this.minRange.value);
    let high = parseFloat(this.maxRange.value);

    // Stop the handles crossing over; whichever one is being dragged wins.
    if (low > high) {
      if (document.activeElement === this.minRange) {
        high = low;
        this.maxRange.value = String(low);
      } else {
        low = high;
        this.minRange.value = String(high);
      }
    }

    const span = rangeMax - rangeMin;
    if (span > 0) {
      this.activeTrack.style.left = `${((low - rangeMin) / span) * 100}%`;
      this.activeTrack.style.width = `${((high - low) / span) * 100}%`;
    }

    if (this.minDisplay) this.minDisplay.textContent = this.symbol + this.formatter.format(low);
    if (this.maxDisplay) this.maxDisplay.textContent = this.symbol + this.formatter.format(high);
  }

  submit() {
    const url = new URL(window.location.href);
    url.searchParams.set('filter.v.price.gte', this.minRange.value);
    url.searchParams.set('filter.v.price.lte', this.maxRange.value);
    // The narrowed result set won't have as many pages as the old one.
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  }
}

if (!customElements.get('price-slider-facet')) {
  customElements.define('price-slider-facet', PriceSliderFacet);
}
